// Builds the `runner.lifecycle` capability handler. No serve.ts import keeps the
// dependency direction acyclic; getters (not values) because serve.ts lazily
// (re)assigns resourceManager + rebuilds the orchestrator on driver recreate.
import type { ConnectorManager } from "@skaile/workspaces/connectors";
import type { CompactionOrchestrator } from "./compaction/index.js";
import type { RunnerCapabilityHandlers } from "./runner-capabilities.js";

export interface LifecycleHandlerDeps {
  /** The current compaction orchestrator, or null before the driver is built. */
  getCompactionOrchestrator: () => CompactionOrchestrator | null;
  /** The live unified connector manager, or null before the session is built. */
  getResourceManager: () => ConnectorManager | null;
  log: (msg: string) => void;
}

/**
 * Build the `runner.lifecycle` handler. Each phase is best-effort: a missing
 * dependency (orchestrator/manager not yet built) logs and no-ops rather than
 * throwing, so the platform's lifecycle invoke always resolves cleanly.
 */
export function buildLifecycleHandler(
  deps: LifecycleHandlerDeps,
): NonNullable<RunnerCapabilityHandlers["lifecycle"]> {
  const { getCompactionOrchestrator, getResourceManager, log } = deps;

  return async ({ phase }) => {
    switch (phase) {
      case "compact": {
        const orchestrator = getCompactionOrchestrator();
        // 'manual' (not 'threshold') — this is a platform-driven compaction.
        if (orchestrator) await orchestrator.compact("manual");
        else log("[lifecycle] compact: no compaction orchestrator — skipped");
        break;
      }
      case "hibernate": {
        const manager = getResourceManager();
        if (manager) await manager.hibernateAll();
        else log("[lifecycle] hibernate: no resource manager — skipped");
        break;
      }
      case "close": {
        const manager = getResourceManager();
        if (manager) await manager.closeAll();
        else log("[lifecycle] close: no resource manager — skipped");
        break;
      }
    }
    return { ok: true };
  };
}
